import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  upsertMomopay,
  updatePaymentClientStatus,
  deletePaymentClient,
  listPaymentClients,
  type ErrorResponse,
  type PaymentClientSummary,
} from '@vn-propane/shared';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

interface MomoPayCardProps {
  orgId: string;
  paymentClient: PaymentClientSummary | undefined;
  onUpdate: (clients: PaymentClientSummary[]) => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

export function MomoPayCard({
  orgId,
  paymentClient,
  onUpdate,
  onSuccess,
  onError,
}: MomoPayCardProps) {
  const { t } = useTranslation(['admin']);
  const [expanded, setExpanded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [partnerCode, setPartnerCode] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [sandbox, setSandbox] = useState(true);

  const isConfigured = paymentClient?.isConfigured ?? false;
  const isEnabled = paymentClient?.isEnabled ?? false;

  const refreshPaymentClients = async () => {
    const result = await listPaymentClients({ path: { org_id: orgId } });
    if (result.data) {
      onUpdate(result.data.data);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const result = await upsertMomopay({
        path: { org_id: orgId },
        body: {
          partnerCode,
          accessKey,
          secretKey,
          sandbox,
          isEnabled: true,
        },
      });

      if (result.data) {
        onSuccess(t('payment.momopay.saveSuccess'));
        await refreshPaymentClients();
        setExpanded(false);
        setPartnerCode('');
        setAccessKey('');
        setSecretKey('');
        return;
      }

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        onError(errorResponse.error?.message || t('payment.momopay.saveFailed'));
      }
    } catch {
      onError(t('payment.unexpectedError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = async (enabled: boolean) => {
    try {
      const result = await updatePaymentClientStatus({
        path: { org_id: orgId, provider: 'momopay' },
        body: { isEnabled: enabled },
      });

      if (result.data) {
        const status = enabled ? t('payment.enabled') : t('payment.disabled');
        onSuccess(t('payment.statusUpdated', { provider: t('payment.momopay.title'), status }));
        await refreshPaymentClients();
        return;
      }

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        onError(errorResponse.error?.message || t('payment.updateFailed'));
      }
    } catch {
      onError(t('payment.unexpectedError'));
    }
  };

  const handleDelete = async () => {
    try {
      const result = await deletePaymentClient({
        path: { org_id: orgId, provider: 'momopay' },
      });

      if (result.data) {
        onSuccess(t('payment.configDeleted', { provider: t('payment.momopay.title') }));
        await refreshPaymentClients();
        return;
      }

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        onError(errorResponse.error?.message || t('payment.deleteFailed'));
      }
    } catch {
      onError(t('payment.unexpectedError'));
    }
  };

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center text-pink-600 font-bold">
            M
          </div>
          <div>
            <h4 className="font-medium">{t('payment.momopay.title')}</h4>
            <p className="text-sm text-muted-foreground">
              {isConfigured ? t('payment.configured') : t('payment.notConfigured')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isConfigured && (
            <>
              <Switch
                checked={isEnabled}
                onCheckedChange={handleToggle}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDelete}
              >
                {t('payment.delete')}
              </Button>
            </>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded
              ? t('payment.cancel')
              : isConfigured
                ? t('payment.update')
                : t('payment.configure')}
          </Button>
        </div>
      </div>

      {expanded && (
        <form onSubmit={handleSave} className="mt-4 space-y-3 border-t pt-4">
          <div className="space-y-2">
            <Label htmlFor="momo-partner-code">{t('payment.momopay.partnerCode')}</Label>
            <Input
              id="momo-partner-code"
              value={partnerCode}
              onChange={(e) => setPartnerCode(e.target.value)}
              placeholder={t('payment.momopay.partnerCodePlaceholder')}
              required
              disabled={isSaving}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="momo-access-key">{t('payment.momopay.accessKey')}</Label>
            <Input
              id="momo-access-key"
              value={accessKey}
              onChange={(e) => setAccessKey(e.target.value)}
              placeholder={t('payment.momopay.accessKeyPlaceholder')}
              required
              disabled={isSaving}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="momo-secret-key">{t('payment.momopay.secretKey')}</Label>
            <Input
              id="momo-secret-key"
              type="password"
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              placeholder={t('payment.momopay.secretKeyPlaceholder')}
              required
              disabled={isSaving}
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="momo-sandbox"
              checked={sandbox}
              onCheckedChange={setSandbox}
              disabled={isSaving}
            />
            <Label htmlFor="momo-sandbox">{t('payment.sandboxMode')}</Label>
          </div>
          <Button type="submit" disabled={isSaving}>
            {isSaving ? t('payment.saving') : t('payment.momopay.saveConfig')}
          </Button>
        </form>
      )}
    </div>
  );
}
